<script lang="ts" setup>
import { computed, ref } from 'vue';
import { BAlert, BButton, BModal, BSpinner } from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import { InviteAcceptDocument, InviteType } from '@/gql/graphql';

// --- Props for the component ---
interface Props {
  invite: InviteType;
  show: boolean;
  adminAccess?: boolean;
}
const props = defineProps<Props>();
const emits = defineEmits(['success', 'close']);

// --- Computed Properties ---
const show = computed<boolean>(() => {
  return props.show;
});

// --- Mutation: Accept Invite ---
const mutationShowSuccess = ref(false);
const mutationInProgress = ref(false);
const mutationError = ref<string | null>(null);
const {
  mutate: inviteMutation,
  onDone: onMutationDone,
  onError: onMutationError,
} = useMutation(InviteAcceptDocument);

onMutationDone((res) => {
  mutationInProgress.value = false;
  if (res.data && res.data.inviteAccept) {
    console.debug('Invite accepted:', res.data.inviteAccept);
    // emit event so the parent can refresh the memberships
    mutationShowSuccess.value = true;
    emits('success');
    // close the modal with delay
    setTimeout(() => {
      handleCloseModal();
    }, 2000);
  } else {
    console.error('Invite accept failed:', res.data);
    mutationError.value = `Invite accept failed: ${res.data}`;
  }
});

onMutationError((err) => {
  console.error('Error accepting invite:', err);
  mutationInProgress.value = false;
  mutationError.value = err.message;
});

// --- Handlers ---
function acceptInvite() {
  mutationError.value = null;
  mutationInProgress.value = true;

  if (!props.invite || !props.invite.id) {
    mutationInProgress.value = false;
    mutationError.value = 'Invalid invite data.';
    return;
  }

  inviteMutation({
    id: props.invite.id,
    adminAccess: props.adminAccess || false,
  });
}

function handleCloseModal() {
  emits('close');
}
</script>
<template>
  <b-modal
    v-if="props.invite.organization"
    v-model="show"
    @hide="handleCloseModal"
  >
    <!-- Title -->
    <template #title>
      <font-awesome-icon :icon="['fas', 'user-check']" class="me-2" />
      <strong>Accept Invite</strong>
    </template>
    <!-- Content -->
    <div>
      <p>
        Confirm to accept an invite to join
        <strong>{{ props.invite.organization.name }}</strong> organization.
      </p>

      <!-- Mutation Error Messages -->
      <BAlert v-model="mutationError" variant="danger" dismissible class="mt-3">
        {{ mutationError }}
      </BAlert>
    </div>

    <!-- Footer with action buttons -->
    <template #footer>
      <BButton variant="secondary" @click="handleCloseModal" class="me-2">
        <span>Cancel</span>
      </BButton>
      <BButton
        variant="primary"
        :disabled="mutationInProgress || mutationShowSuccess"
        @click="acceptInvite()"
      >
        <span v-if="mutationInProgress">
          <b-spinner small label="" class="me-1"></b-spinner>
          Accepting invite
        </span>
        <span v-else-if="mutationShowSuccess">
          <font-awesome-icon :icon="['fas', 'check-double']" class="me-1" />
          Invite accepted
        </span>
        <span v-else>
          <font-awesome-icon :icon="['fas', 'user-check']" class="me-1" />
          Accept invite
        </span>
      </BButton>
    </template>
  </b-modal>
</template>
